import React, { useEffect, useState } from "react";
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Typography,
    CircularProgress,
    TableSortLabel,
    Button,
    Paper,
    IconButton,
    Menu,
    MenuItem,
    Tooltip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Container,
} from "@mui/material";
import useSWR, { mutate } from "swr";
import { useNavigate } from "react-router-dom";
import API_ENDPOINTS from "../../apis/endpoints";
import SearchInput from "../../components/SearchInput";
import MoreVertIcon from "@mui/icons-material/MoreVert";

import useAuth from "../../context";

const ThreeDotsMenu = ({ onDelete, onRename }) => {
    const [anchorEl, setAnchorEl] = useState(null);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <>
            <Tooltip title="Actions">
                <IconButton onClick={handleClick}>
                    <MoreVertIcon />
                </IconButton>
            </Tooltip>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
            >
                <MenuItem
                    onClick={() => {
                        onRename();
                        handleClose();
                    }}
                >
                    Rename
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        onDelete();
                        handleClose();
                    }}
                >
                    Delete
                </MenuItem>
            </Menu>
        </>
    );
};

const DeleteConfirmationModal = ({ open, onClose, onConfirm }) => (
    <Dialog open={open} onClose={onClose}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
            Are you sure you want to delete this voice model?
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
            <Button onClick={onConfirm} color="error">
                Delete
            </Button>
        </DialogActions>
    </Dialog>
);

const RenameModal = ({ open, onClose, onConfirm, currentName }) => {
    const [newName, setNewName] = useState(currentName);

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>Rename Sound Name</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    margin="dense"
                    label="New Name"
                    type="text"
                    fullWidth
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={() => onConfirm(newName)}>Rename</Button>
            </DialogActions>
        </Dialog>
    );
};

const AiAvatarList = ({ onModelSelect }) => {
    const navigate = useNavigate();
    const auth = useAuth();
    const storedDeviceId = localStorage.getItem("device-id");
    const [searchTerm, setSearchTerm] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [sortColumn, setSortColumn] = useState("created_at");
    const [sortDirection, setSortDirection] = useState("desc");
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [renameModalOpen, setRenameModalOpen] = useState(false);
    const [selectedModel, setSelectedModel] = useState(null);
    const [playingModelId, setPlayingModelId] = useState(null);

    const getStatusColor = (status) => {
        switch (status.toUpperCase()) {
            case "MODELLING":
            case "PENDING":
                return "warning.main";
            case "FAILED":
                return "error.main";
            case "READY":
                return "success.main";
            default:
                return "text.primary";
        }
    };

    const queryParam = searchQuery ? `&query=${searchQuery}` : "";
    const sortParam = `&sort=${
        sortDirection === "desc" ? `-${sortColumn}` : sortColumn
    }`;

    const { data, isLoading } = useSWR(
        `${API_ENDPOINTS.AI_MODEL_AVATAR}?limit=${rowsPerPage}&offset=${
            page * rowsPerPage
        }${queryParam}${sortParam}`
    );

    useEffect(() => {
        if (!isLoading && (!data || data?.length === 0)) {
            navigate("landing");
        }
    }, [isLoading, data, navigate]);

    const voiceModels = data?.results ?? [];
    const totalVoiceModels = data?.meta?.total ?? 0;

    const handlePlayPause = (modelId, sampleUrl) => {
        if (playingModelId === modelId) {
            setPlayingModelId(null);
        } else {
            if (playingModelId) {
                // Implement logic to stop currently playing audio
            }
            setPlayingModelId(modelId);
            const audio = new Audio(sampleUrl);
            audio.play();
        }
    };

    const handleSearch = () => {
        setSearchQuery(searchTerm);
        setPage(0);
    };

    const handleSortRequest = (column) => {
        const isAsc = sortColumn === column && sortDirection === "asc";
        setSortDirection(isAsc ? "desc" : "asc");
        setSortColumn(column);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };
    const handleRowClick = (model) => {
        if (model.status === "READY") {
            navigate(`/dashboard/create-avatar/chat/${model.id}`);
        }
    };

    const confirmDelete = async () => {
        try {
            await fetch(`${API_ENDPOINTS.AI_MODEL_AVATAR}${selectedModel}/`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${auth.accessToken}`,
                    "x-cc-device-id": storedDeviceId,
                },
            });
            mutate(
                `${API_ENDPOINTS.AI_MODEL_AVATAR}?limit=${rowsPerPage}&offset=${
                    page * rowsPerPage
                }${queryParam}${sortParam}`
            );
        } catch (error) {
            console.error("Error deleting model:", error);
        }
        setDeleteModalOpen(false);
    };

    const confirmRename = async (newName) => {
        try {
            await fetch(
                `${API_ENDPOINTS.AI_MODEL_AVATAR}${selectedModel.id}/`,
                {
                    method: "PATCH",
                    headers: {
                        Authorization: `Bearer ${auth.accessToken}`,
                        "x-cc-device-id": storedDeviceId,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ name: newName }),
                }
            );
            mutate(
                `${API_ENDPOINTS.AI_MODEL_AVATAR}?limit=${rowsPerPage}&offset=${
                    page * rowsPerPage
                }${queryParam}${sortParam}`
            );
        } catch (error) {
            console.error("Error renaming model:", error);
        }
        setRenameModalOpen(false);
    };

    return (
        <Container style={{ padding: 0, width: "100%" }}>
            <Box className="voice-cloning-list-container">
                <Typography variant="featureAccent" gutterBottom>
                    AI Avatar List
                </Typography>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                    sx={{ width: "100%", justifyContent: "flex-end" }}
                >
                    {/* <SearchInput
                        onSearch={handleSearch}
                        placeHolder="Sound..."
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        sx={{ width: "70vw" }}
                    /> */}

                    <Button
                        variant="contained"
                        size="large"
                        onClick={() => navigate("create")}
                        // sx={{
                        //     textTransform: "none",
                        //     pointerEvents: "none", // Ensures the tooltip works even when the button is disabled
                        // }}
                    >
                        Create New
                    </Button>
                </Box>
                {isLoading ? (
                    <Box className="loader-container">
                        <CircularProgress />
                    </Box>
                ) : (
                    <Paper className="table-paper">
                        <TableContainer className="table-container">
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        {[
                                            "avatar name",
                                            "created on",
                                            "created by",
                                            "status",
                                            "Actions",
                                        ].map((column) => (
                                            <TableCell
                                                key={column}
                                                className="table-header-cell"
                                            >
                                                <TableSortLabel
                                                    active={
                                                        sortColumn === column
                                                    }
                                                    direction={
                                                        sortColumn === column
                                                            ? sortDirection
                                                            : "asc"
                                                    }
                                                    onClick={() =>
                                                        handleSortRequest(
                                                            column
                                                        )
                                                    }
                                                >
                                                    {column
                                                        .charAt(0)
                                                        .toUpperCase() +
                                                        column
                                                            .slice(1)
                                                            .replace(/_/g, " ")}
                                                </TableSortLabel>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {voiceModels.length > 0 ? (
                                        voiceModels.map((model) => (
                                            <TableRow
                                                key={model.id}
                                                className="table-row"
                                                style={{
                                                    cursor:
                                                        model.status === "READY"
                                                            ? "pointer"
                                                            : "default",
                                                }}
                                            >
                                                {[
                                                    "name",
                                                    "created_at",
                                                    "user_name",
                                                ].map((column) => (
                                                    <>
                                                        <TableCell
                                                            key={column}
                                                            className="table-cell"
                                                            onClick={() =>
                                                                handleRowClick(
                                                                    model
                                                                )
                                                            }
                                                        >
                                                            {column ===
                                                            "created_at"
                                                                ? new Date(
                                                                      model[
                                                                          column
                                                                      ]
                                                                  ).toLocaleString()
                                                                : model[
                                                                      column
                                                                  ] || "-"}
                                                        </TableCell>
                                                    </>
                                                ))}
                                                <TableCell>
                                                    <Typography
                                                        color={getStatusColor(
                                                            model.status
                                                        )}
                                                    >
                                                        {model.status ===
                                                        "PENDING"
                                                            ? "MODELLING"
                                                            : model.status}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell>
                                                    <ThreeDotsMenu
                                                        onDelete={() => {
                                                            setSelectedModel(
                                                                model.id
                                                            );
                                                            setDeleteModalOpen(
                                                                true
                                                            );
                                                        }}
                                                        onRename={() => {
                                                            setSelectedModel({
                                                                id: model.id,
                                                                currentName:
                                                                    model.name,
                                                            });
                                                            setRenameModalOpen(
                                                                true
                                                            );
                                                        }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell
                                                colSpan={6}
                                                align="center"
                                            >
                                                No voice models found.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={totalVoiceModels}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </Paper>
                )}
            </Box>
            <DeleteConfirmationModal
                open={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={confirmDelete}
            />
            {selectedModel && (
                <RenameModal
                    open={renameModalOpen}
                    onClose={() => setRenameModalOpen(false)}
                    onConfirm={confirmRename}
                    currentName={selectedModel.currentName}
                />
            )}
        </Container>
    );
};

export default AiAvatarList;
